import React, { useState } from 'react';
import * as Yup from 'yup';
import { useValidation } from '../../../../hooks/useValidation';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import Tooltip from '../../../Tooltip/Tooltip';
import Modal from '../../../Modal/Modal';
import { formatPhoneNumber } from '../../registrationUtils';
import styles from './AddUsers.module.css';
import Badge from '../../../Badge/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPenToSquare, faTrashCan, faPlus } from '@fortawesome/free-solid-svg-icons';

// Status chips for a user's role(s) — shared by the desktop table and the
// mobile cards so status always reads the same way.
const RoleBadges = ({ userType }) => (
  <>
    {userType?.is_manager && <Badge text="Manager" />}
    {userType?.is_on_call_provider && <Badge text="On-Call Provider" type="secondary" />}
  </>
);

const userSchema = Yup.object({
    first_name: Yup.string().required('First name is required'),
    last_name: Yup.string().required('Last name is required'),
    email: Yup.string().email('Must be a valid email').required('Email is required'),
    phone: Yup.string().matches(/^\(\d{3}\) \d{3}-\d{4}$/, 'Phone number must be (XXX) XXX-XXXX').required('Phone is required'),
});

const AddUsers = ({ users, onUserAdd, onUserDelete, onUserUpdate, licensingFee}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [errors, validate, clearErrors] = useValidation(userSchema);
    const isMobile = useMediaQuery('(max-width: 768px)');

    const initialUserState = {
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        user_type: {
            is_manager: false,
            is_on_call_provider: false,
        }
    };

    const [editingUser, setEditingUser] = useState(null);
    const [currentUserData, setCurrentUserData] = useState(initialUserState);
    const [localError, setLocalError] = useState(null);

    const handleEdit = (user) => {
        setEditingUser(user);
        setCurrentUserData(user);
        setLocalError(null);
        setIsModalOpen(true);
    };

    const handleOpenAddModal = () => {
        setEditingUser(null);
        setCurrentUserData(initialUserState);
        setLocalError(null);
        setIsModalOpen(true);
    }

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setLocalError(null);
        clearErrors();
    }

    const handleFormChange = (e) => {
        let { name, value } = e.target;
        if (name === 'phone') {
            value = formatPhoneNumber(value);
        }
        setCurrentUserData(prev => ({ ...prev, [name]: value}));
    };

    const handleUserTypeChange = (e) => {
        const { name, checked } = e.target;
        setCurrentUserData(prev => ({
            ...prev,
            user_type: {
                ...prev.user_type,
                [name]: checked
            }
        }));
    };

    const handleSave = async () => {
        const isValid = await validate(currentUserData);
        if (!isValid) {
            setLocalError('Please correct the errors in the form.');
            return;
        }

        if (!currentUserData.user_type.is_manager && !currentUserData.user_type.is_on_call_provider) {
            setLocalError('Please select at least one role.');
            return;
        }

        if (editingUser) {
            onUserUpdate(currentUserData);
        } else {
            onUserAdd(currentUserData);
        }
        handleCloseModal();
    };

    return (
        <div>
            <h2 className={styles.stepTitle}>Users</h2>
            <h3 className={styles.stepSubtitle}>* At least one manager is required in your list of provided users</h3>
            <h3 className={styles.stepSubtitle}>* Users can be managed from your MedX Portal after account activation</h3>
            <div className={styles.tableContainer}>
                {users.length === 0 ? (
                    <div className={styles.emptyState}>
                        No users added yet. {isMobile ? 'Tap' : 'Click'} "Add User" to get started.
                    </div>
                ) : isMobile ? (
                    <div className={styles.userCardList}>
                        {users.map(user => (
                            <div key={user.id} className={styles.userCard}>
                                <div className={styles.userCardHeader}>
                                    <span className={styles.userCardName}>
                                        {user.first_name} {user.last_name}
                                    </span>
                                    <div className={styles.userCardBadges}>
                                        <RoleBadges userType={user.user_type} />
                                    </div>
                                </div>
                                <div className={styles.userCardRow}>
                                    <span className={styles.userCardLabel}>Phone</span>
                                    <span>{formatPhoneNumber(user.phone)}</span>
                                </div>
                                <div className={styles.userCardRow}>
                                    <span className={styles.userCardLabel}>Email</span>
                                    <span className={styles.userCardEmail}>{user.email}</span>
                                </div>
                                <div className={styles.userCardActions}>
                                    <button className={styles.cardActionButton} onClick={() => handleEdit(user)}>
                                        <FontAwesomeIcon icon={faPenToSquare} /> Edit
                                    </button>
                                    <button className={`${styles.cardActionButton} ${styles.cardActionButtonDelete}`} onClick={() => onUserDelete(user.id)}>
                                        <FontAwesomeIcon icon={faTrashCan} /> Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <table className={styles.userTable}>
                        <thead>
                            <tr>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th className={styles.phoneCol}>Phone</th>
                                <th>Email</th>
                                <th>User Type</th>
                                <th className={styles.actionsCol}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id}>
                                    <td className={styles.nameCell}>{user.first_name}</td>
                                    <td className={styles.nameCell}>{user.last_name}</td>
                                    <td className={styles.phoneCell}>{formatPhoneNumber(user.phone)}</td>
                                    <td className={styles.emailCell}>{user.email}</td>
                                    <td>
                                        <div className={styles.badgeStack}>
                                            <RoleBadges userType={user.user_type} />
                                        </div>
                                    </td>
                                    <td>
                                        <div className={styles.rowActions}>
                                            <button
                                                type="button"
                                                className={styles.iconButton}
                                                onClick={() => handleEdit(user)}
                                                aria-label={`Edit ${user.first_name} ${user.last_name}`}
                                                title="Edit"
                                            >
                                                <FontAwesomeIcon icon={faPenToSquare} />
                                            </button>
                                            <button
                                                type="button"
                                                className={`${styles.iconButton} ${styles.iconButtonDelete}`}
                                                onClick={() => onUserDelete(user.id)}
                                                aria-label={`Delete ${user.first_name} ${user.last_name}`}
                                                title="Delete"
                                            >
                                                <FontAwesomeIcon icon={faTrashCan} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            <div className={styles.addUserButtonContainer}>
                <button className={styles.addUserButton} onClick={handleOpenAddModal}>
                    <FontAwesomeIcon icon={faPlus} /> Add User
                </button>
            </div>
            <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={editingUser ? 'Edit User' : 'Add New User'}
                footer={
                    <>
                        <button type="button" className={styles.secondaryButton} onClick={handleCloseModal}>
                            Cancel
                        </button>
                        <button type="button" className={styles.submitButton} onClick={handleSave}>
                            <FontAwesomeIcon icon={faCheck} className={styles.submitButtonIcon} />
                            Save
                        </button>
                    </>
                }
            >
                {localError && (
                    <div className={styles.inlineError}>
                        {localError}
                        <span className={styles.inlineErrorClose} onClick={() => setLocalError(null)}>&times;</span>
                    </div>
                )}
                <div className={styles.inputGrid}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="first_name">First Name</label>
                        <input type="text" id="first_name" name="first_name" value={currentUserData.first_name} onChange={handleFormChange} />
                        {errors.first_name && <div className={styles.errorMessage}>{errors.first_name}</div>}
                    </div>
                    <div className={styles.inputGroup}>
                        <label htmlFor="last_name">Last Name</label>
                        <input type="text" id="last_name" name="last_name" value={currentUserData.last_name} onChange={handleFormChange} />
                        {errors.last_name && <div className={styles.errorMessage}>{errors.last_name}</div>}
                    </div>
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="email">Email</label>
                    <input type="email" id="email" name="email" value={currentUserData.email} onChange={handleFormChange} />
                    {errors.email && <div className={styles.errorMessage}>{errors.email}</div>}
                </div>
                <div className={styles.inputGroup}>
                    <label htmlFor="phone">Phone</label>
                    <input type="tel" id="phone" name="phone" value={currentUserData.phone} onChange={handleFormChange} />
                    {errors.phone && <div className={styles.errorMessage}>{errors.phone}</div>}
                </div>
                <div className={styles.inputGroup}>
                    <label>Role(s)</label>
                    <div className={styles.checkboxGroup}>
                        <Tooltip text="A manager is someone who has administrative access, can listen to calls, access admin portal, calendar, etc">
                            <label className={styles.checkboxLabel} htmlFor="is_manager">
                                <input type="checkbox" id="is_manager" name="is_manager" checked={currentUserData.user_type.is_manager} onChange={handleUserTypeChange} />
                                Manager
                            </label>
                        </Tooltip>
                        <Tooltip text={licensingFee ? "A user who takes after hours calls. Will incur licensing fee." : "A user who takes after hours calls."}>
                            <label className={styles.checkboxLabel} htmlFor="is_on_call_provider">
                                <input
                                    type="checkbox"
                                    id="is_on_call_provider"
                                    name="is_on_call_provider"
                                    checked={currentUserData.user_type.is_on_call_provider}
                                    onChange={handleUserTypeChange}
                                />
                                On-Call Provider
                            </label>
                        </Tooltip>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AddUsers;
